// ============================================================================
// The Analysis panel shell.
// ----------------------------------------------------------------------------
// This component reads ONLY PanelDef + PanelSeries + Cell. It never knows which
// panel it is drawing, and there is no panel-specific branch anywhere in it.
// That is what makes "a new panel is one file plus one registry entry" true: if
// a panel needed a special case here, every future panel would need one too, and
// the registry would decay into a switch statement.
//
// Three rows, always, on every panel: head, body, stamp. The stamp is in the same
// slot at the same size on every panel, on screen and on paper, because a figure
// that will not say what it is made of has not earned the space.
// ============================================================================

import React from 'react';
import type { Cell, PanelDef, PanelSeries, AnalysisContext, PanelReadiness } from '../../services/analysis/registry';
import { money, moneyCompact } from '../../services/analysis/model';

interface Props {
    def: PanelDef;
    series: PanelSeries;
    ctx: AnalysisContext;
    span: number;
    /** Opens the evidence drawer for a mark. Wired in slice 2; optional until then. */
    onDrill?: (def: PanelDef, cell: Cell) => void;
    /** Opens the drawer for the whole panel from its stamp. Slice 2. */
    onStamp?: (def: PanelDef) => void;
}

function toneOf(cell: Cell): string | undefined {
    return cell.tone;
}

/**
 * How many bars the CHART shows. The panel emits every ranked cell so the CSV,
 * the table view and the export stay complete; the cap is a rendering decision
 * and lives here, where the span is known. A taller panel shows more rows.
 */
function chartRowCap(span: number): number {
    return span >= 12 ? 15 : 6;
}

/** Bars, either orientation. One geometry; the orientation flips the axis in CSS. */
function Bars({ series, def, span, onDrill }: { series: PanelSeries; def: PanelDef; span: number; onDrill?: Props['onDrill'] }) {
    const column = def.orientation === 'column';
    // Column bars are a time series: every slot must show or the axis lies.
    const shown = column ? series.cells : series.cells.slice(0, chartRowCap(span));
    return (
        <div className={column ? 'an-bars an-bars--column' : 'an-bars an-bars--row'}>
            {shown.map((cell) => (
                <button
                    key={cell.key}
                    type="button"
                    className="qm-inline-action-btn an-hbar"
                    onClick={() => onDrill?.(def, cell)}
                    title={`${cell.label}: ${cell.count} job${cell.count === 1 ? '' : 's'}, ${money(cell.value)} ex GST`}
                    aria-label={`${cell.label}, ${cell.count} jobs, ${money(cell.value)} ex GST. Show the rows behind this.`}
                >
                    <span className="an-hbar__label">{cell.label}</span>
                    <span className="an-hbar__track">
                        <span
                            className="an-hbar__fill"
                            style={{ '--an-w': `${Math.max(0, Math.min(1, cell.weight)) * 100}%` } as React.CSSProperties}
                        />
                    </span>
                    <span className="an-hbar__num">{cell.count}</span>
                    <span className="an-hbar__money">{moneyCompact(cell.value)}</span>
                </button>
            ))}
        </div>
    );
}

/** Matrix: lanes down the side, bands across the top, one clickable cell each. */
function Matrix({ series, def, onDrill }: { series: PanelSeries; def: PanelDef; onDrill?: Props['onDrill'] }) {
    const rowKeys = series.rowKeys || [];
    const colKeys = series.colKeys || [];
    const at = (rowKey: string, colKey: string) =>
        series.cells.find((c) => c.rowKey === rowKey && c.colKey === colKey);
    return (
        <div className="an-matrix">
            <div className="an-matrix__row an-matrix__row--head">
                <span className="an-matrix__rowlabel" />
                {colKeys.map((c) => (
                    <span key={c.key} className="an-matrix__collabel">{c.label}</span>
                ))}
            </div>
            {rowKeys.map((r) => (
                <div key={r.key} className="an-matrix__row">
                    <span className="an-matrix__rowlabel">
                        <span className="an-matrix__dot" style={{ background: r.tone }} aria-hidden="true" />
                        {r.label}
                    </span>
                    {colKeys.map((c) => {
                        const cell = at(r.key, c.key);
                        if (!cell) return <span key={c.key} className="an-matrix__cell an-matrix__cell--empty">-</span>;
                        return (
                            <button
                                key={c.key}
                                type="button"
                                className="qm-inline-action-btn an-matrix__cell"
                                data-strong={cell.weight > 0.55 ? 'true' : undefined}
                                style={{ '--an-tint': `${Math.max(0, Math.min(1, cell.weight)) * 0.22}` } as React.CSSProperties}
                                onClick={() => onDrill?.(def, cell)}
                                title={`${r.label}, ${c.label}: ${cell.count} job${cell.count === 1 ? '' : 's'}, ${money(cell.value)} ex GST`}
                                aria-label={`${r.label}, ${c.label}, ${cell.count} jobs, ${money(cell.value)} ex GST. Show the rows behind this.`}
                            >
                                <span className="an-matrix__count">{cell.count}</span>
                                <span className="an-matrix__value">{moneyCompact(cell.value)}</span>
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

/** Figures: a short row of counted facts, each a door. */
function Figures({ series, def, onDrill }: { series: PanelSeries; def: PanelDef; onDrill?: Props['onDrill'] }) {
    return (
        <div className="an-figures">
            {series.cells.map((cell) => (
                <button
                    key={cell.key}
                    type="button"
                    className="qm-inline-action-btn an-figure"
                    onClick={() => onDrill?.(def, cell)}
                    aria-label={`${cell.label}, ${cell.count}. Show the rows behind this.`}
                >
                    <span className="an-figure__num" style={{ color: toneOf(cell) }}>{cell.count}</span>
                    <span className="an-figure__label">{cell.label}</span>
                </button>
            ))}
        </div>
    );
}

export default function AnalysisPanel({ def, series, ctx, span, onDrill, onStamp }: Props) {
    const readiness: PanelReadiness = def.readiness(series, ctx);
    const activation = def.activation(series, ctx);
    const stamp = def.stamp(series, ctx);
    const isDormant = readiness === 'dormant';
    const empty = series.cells.length === 0;
    // Row bars are the only mark the shell caps; a column series is a time axis
    // and must show every slot.
    const shownCells = def.mark === 'bars' && def.orientation !== 'column'
        ? Math.min(series.cells.length, chartRowCap(span))
        : series.cells.length;
    const hiddenCells = series.cells.length - shownCells;

    return (
        <section
            className="an-panel"
            data-span={span}
            data-slim={def.slim ? 'true' : undefined}
            data-lead={def.id === 'ageing-matrix' ? 'true' : undefined}
            data-readiness={readiness}
            aria-labelledby={`an-h-${def.id}`}
        >
            {/* Not a <header>: the global stylesheet paints every <header> with a
                dark gradient and !important, which would make this light card
                unreadable. HARD RULE 1. */}
            {/* The truncation note lives in the HEAD, not the body. A "N more"
                line inside a 136px body costs a whole bar row, which is what
                clipped the sixth trade; and "6 of 14" beside the title is better
                copy anyway, because it says how many there are rather than how
                many are missing. Never truncate silently: a chart showing six of
                fourteen with no note reads as "there are six". */}
            <div className="an-panel__head">
                <h2 id={`an-h-${def.id}`}>{def.title}</h2>
                <span className="an-panel__hint">{def.hint}</span>
                {!def.respectsPeriod
                    ? <span className="an-panel__chip">all time</span>
                    : hiddenCells > 0
                        ? <span className="an-panel__chip">{shownCells} of {series.cells.length}</span>
                        : null}
            </div>

            <div className="an-panel__body">
                {series.headline && (
                    <div className="an-lead">
                        <div className="an-lead__figure">{series.headline.figure}</div>
                        <div className="an-lead__caps">{series.headline.caps}</div>
                        <div className="an-lead__clause">{series.headline.clause}</div>
                    </div>
                )}

                {empty ? (
                    <p className="an-empty">{series.emptySeries || activation || 'Nothing to show yet.'}</p>
                ) : def.mark === 'matrix' ? (
                    <Matrix series={series} def={def} onDrill={onDrill} />
                ) : def.mark === 'bars' ? (
                    <Bars series={series} def={def} span={span} onDrill={onDrill} />
                ) : def.mark === 'figures' ? (
                    <Figures series={series} def={def} onDrill={onDrill} />
                ) : null}

                {/* A thin or dormant panel says what would switch it on, beside its
                    real figures rather than instead of them. An empty state that
                    only apologises teaches the operator nothing. */}
                {activation && !empty && <p className="an-activation">{activation}</p>}
            </div>

            {isDormant ? (
                <span className="an-stamp" aria-hidden="false">{stamp}</span>
            ) : (
                <button
                    type="button"
                    className="qm-inline-action-btn an-stamp"
                    onClick={() => onStamp?.(def)}
                    title="Show every row behind this panel"
                >{stamp}</button>
            )}
        </section>
    );
}
